import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { refreshProfile } from '../actions/profileActions';
import { refreshOffers } from '../actions/offerActions';
import Offers from '../components/pages/Offers/Offers';
import { withPusher } from '../contexts/PusherContext';
import { PUSHER } from '../config/constants';
import { getGlobalNotifications } from '../actions/globalNotificationActions';
import { OffersProvider } from '../contexts/OffersContext';
import { PER_PAGE, ORDER_BY } from '../constants/offers';
import { useParams } from 'react-router-dom';
import { RootState } from '../store';
import MasterConfig from 'config/Master';

interface IOffersContainerProps {
  bindListener: (listener: any) => void;
  stopListening: (listener: any) => void;
  onRealtimeEvent: (event: any, callback: (data: any) => void) => void;
}

const OffersContainer: React.FC<IOffersContainerProps> = ({ bindListener, stopListening, onRealtimeEvent }) => {
  const { offerType = 'new' } = useParams();

  const dispatch = useDispatch();
  const meta = useSelector((state: RootState) => state.meta);
  const offerList = useSelector((state: RootState) => state.offers[offerType]);

  const [lastPage, setLastPage] = useState(offerType);
  const [isRealtimeFetching, setIsRealtimeFetching] = useState(false);

  const isFirstCall = meta[`offer_${offerType}_current_page`] <= 1;
  const memoisedCurrentPage = useMemo(() => meta[`offer_${offerType}_current_page`], [meta]);

  const fetchOffersByType = (route, orderType = ORDER_BY, firstCall = true, perPage = PER_PAGE, isRealtime = false) => {
    dispatch(refreshProfile());
    dispatch(refreshOffers(route, orderType, firstCall, perPage, isRealtime));
  };

  useEffect(() => {
    if (!isFirstCall && memoisedCurrentPage > 1 && offerList.length < 1) {
      fetchOffersByType(offerType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFirstCall, offerType, offerList, memoisedCurrentPage]);

  useEffect(() => {
    if (isFirstCall) {
      fetchOffersByType(offerType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFirstCall, offerType]);

  useEffect(() => {
    bindListener(listen);

    return () => {
      // refresh Global Notification on page unload
      dispatch(getGlobalNotifications());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const next = offerType;

    if (next !== lastPage) {
      setLastPage(next);
    }

    return () => {
      stopListening(listen);
    };
  }, [offerType]);

  const listen = () => {
    const { EVENTS } = PUSHER;

    onRealtimeEvent(EVENTS.ACCOUNT_ALERTS, data => {
      if (MasterConfig.APP_ENV !== 'production') {
        console.log(`Notification Received from Pusher for event - ${EVENTS.ACCOUNT_ALERTS}`, data);
      }

      if (data.type === 'offers' && ['receive', 'suggest', 'cancel', 'update'].includes(data.action)) {
        setIsRealtimeFetching(true);
        fetchOffersByType(offerType, ORDER_BY, true, PER_PAGE, true);
      }
    });
  };

  return (
    <OffersProvider>
      <Offers
        offerType={offerType}
        isFirstCall={isFirstCall}
        isRealtimeFetching={isRealtimeFetching}
        setIsRealtimeFetching={setIsRealtimeFetching}
      />
    </OffersProvider>
  );
};

export default withPusher(OffersContainer);
